import { create, DescMessage, MessageInitShape, MessageShape } from "@bufbuild/protobuf";
import type { UseInfiniteQueryOptions as TanStackUseInfiniteQueryOptions } from "@connectrpc/connect-query";

/**
 * Utility type for infinite query inputs that ensures the page param field is required.
 * Protobuf schemas have optional pagination fields, but useInfiniteQuery expects
 * the pageParamKey field to be required.
 */
export type InfiniteQueryInput<T, ParamKey extends keyof T> = T & {
  [K in ParamKey]-?: NonNullable<T[K]>;
};

/**
 * Creates a protobuf message with a required page param field for use with useInfiniteQuery.
 * This wrapper ensures the returned type has the pagination field as required.
 */
export function createInfiniteQueryInput<
  TSchema extends DescMessage,
  TParamKey extends keyof MessageInitShape<TSchema>,
>(
  schema: TSchema,
  input: MessageInitShape<TSchema> &
    Record<TParamKey, NonNullable<MessageInitShape<TSchema>[TParamKey]>>,
): InfiniteQueryInput<MessageShape<TSchema>, TParamKey> {
  return create(schema, input) as InfiniteQueryInput<MessageShape<TSchema>, TParamKey>;
}

/**
 * Simplified options type for infinite queries that omits the pagination
 * configuration (pageParamKey and getNextPageParam) since those are handled
 * by the hook implementation.
 */
export type UseInfiniteQueryOptions<
  I extends DescMessage,
  O extends DescMessage,
  ParamKey extends keyof MessageInitShape<I>,
> = Omit<TanStackUseInfiniteQueryOptions<I, O, ParamKey>, "pageParamKey" | "getNextPageParam">;
